import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/base/JsonLd';
import { GENRES, comics } from '@/mocks/comics';

type SortKey = 'latest' | 'views' | 'rating';

export default function Category() {
  const [params, setParams] = useSearchParams();
  const activeGenre = params.get('g') || '全部';
  const searchQ = params.get('q') || '';
  const [sort, setSort] = useState<SortKey>('views');
  const [input, setInput] = useState(searchQ);

  const setGenre = (g: string) => {
    const next = new URLSearchParams(params);
    if (g === '全部') next.delete('g');
    else next.set('g', g);
    next.delete('q');
    setParams(next);
    setInput('');
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (input.trim()) next.set('q', input.trim());
    else next.delete('q');
    setParams(next);
  };

  const result = useMemo(() => {
    let list = comics;
    if (activeGenre !== '全部') list = list.filter((c) => c.genre === activeGenre);
    if (searchQ) {
      const kw = searchQ.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.includes(searchQ)) ||
          c.intro.includes(searchQ),
      );
    }
    if (sort === 'views') list = [...list].sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    if (sort === 'latest') list = [...list].sort((a, b) => a.id % 4 - (b.id % 4));
    return list;
  }, [activeGenre, searchQ, sort]);

  const title = `${activeGenre !== '全部' ? `${activeGenre}漫画` : '恋爱漫画'} - 桃花漫画网`;
  const description = `${activeGenre !== '全部' ? `${activeGenre}恋爱漫画分类` : '桃花漫画网全部恋爱漫画'}，现代言情、甜宠少女、校园青春等分类齐全，热门作品实时更新，免费章节多。`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: 'https://cartonplay.github.io/category',
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: '桃花漫画网', url: 'https://cartonplay.github.io/' },
  };

  return (
    <>
      <JsonLd data={schema} />
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-1.5 text-xs text-foreground-400">
          <Link to="/" className="hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-600">{activeGenre === '全部' ? '全部漫画' : `${activeGenre}漫画`}</span>
        </nav>

        <header className="mb-8">
          <h1 className="font-display text-2xl font-bold text-foreground-900 md:text-4xl">
            {activeGenre === '全部' ? '全部恋爱漫画' : `${activeGenre}恋爱漫画`}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">{description}</p>
        </header>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-background-100/70 px-3 py-3 md:px-4">
          {GENRES.map((g) => (
            <button
              key={g}
              onClick={() => setGenre(g)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                activeGenre === g
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'bg-background-50 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              {g}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <form onSubmit={onSearch} className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-1.5">
              <i className="ri-search-line text-sm text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索漫画名/作者"
                className="w-32 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 sm:w-44"
              />
            </form>
          </div>
        </div>

        {/* Sort */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">排序：</span>
          {(
            [
              ['views', '人气最高'],
              ['rating', '评分最高'],
              ['latest', '最新更新'],
            ] as [SortKey, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                sort === key
                  ? 'bg-accent-500 font-medium text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-secondary-200'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="ml-auto text-xs text-foreground-400">共 {result.length} 部作品</span>
        </div>

        {/* Grid */}
        {result.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {result.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="mt-16 flex flex-col items-center py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="mt-4 font-display text-xl font-bold text-foreground-800">没有找到相关漫画</h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              换个关键词或题材试试吧，也可以浏览全部漫画发现更多作品。
            </p>
            <button
              onClick={() => {
                setGenre('全部');
                setInput('');
              }}
              className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部漫画
            </button>
          </div>
        )}
      </section>
    </>
  );
}